import * as React from 'react';
import { useCameraSnapshot, useCameraStreamToken } from '@cguard/api-client/hooks/cameras';
import { getCguardAccessToken } from '@cguard/auth';
import { useTranslation } from 'react-i18next';
import { HlsVideoPlayer, type HlsPlayerState } from './hls-video-player';
import {
  WhepVideoPlayer,
  type WhepVideoPlayerProps,
  type WhepTransportStatus,
} from './whep-video-player';
import { readTotalVideoFrames, subscribeFrameHealth, FRAME_STALL_AFTER_MS } from './frame-health';

type CameraKey = NonNullable<Parameters<typeof useCameraStreamToken>[0]>;
export type LiveCameraState = HlsPlayerState | 'still';

type LiveCameraPlayerProps = WhepVideoPlayerProps & {
  onStateChange?: (state: LiveCameraState) => void;
};

function isCameraKey(value: string): value is CameraKey {
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(value);
}

/** One transport at a time. A camera change starts a fresh WHEP attempt. */
export function LiveCameraPlayer(props: LiveCameraPlayerProps): React.JSX.Element {
  return <LiveCameraSession key={props.cameraId} {...props} />;
}

function LiveCameraSession({
  onStateChange,
  onTransportStatusChange,
  ...props
}: LiveCameraPlayerProps): React.JSX.Element {
  const stateCallback = React.useRef(onStateChange);
  stateCallback.current = onStateChange;
  const reportState = React.useCallback(
    (state: LiveCameraState) => stateCallback.current?.(state),
    [],
  );
  const [transport, setTransport] = React.useState<'whep' | 'hls' | 'still'>('whep');
  const [receivedWhepFrame, setReceivedWhepFrame] = React.useState(false);
  const useHls = React.useCallback(() => setTransport('hls'), []);
  const useStill = React.useCallback(() => setTransport('still'), []);
  React.useEffect(() => {
    if (transport !== 'whep' || receivedWhepFrame) return;
    const timer = setTimeout(useHls, 15_000);
    return () => clearTimeout(timer);
  }, [transport, receivedWhepFrame, useHls]);
  const whepStatus = React.useCallback(
    (status: WhepTransportStatus) => {
      if (status === 'live') setReceivedWhepFrame(true);
      reportState(status === 'live' ? 'playing' : status === 'connecting' ? 'loading' : 'offline');
      onTransportStatusChange?.(status);
    },
    [reportState, onTransportStatusChange],
  );
  if (transport === 'whep') {
    return (
      <WhepVideoPlayer
        {...props}
        getAccessToken={props.getAccessToken ?? getCguardAccessToken}
        onUnavailable={useHls}
        onTransportStatusChange={whepStatus}
      />
    );
  }
  const cameraId = isCameraKey(props.cameraId) ? props.cameraId : null;
  if (transport === 'hls') {
    return (
      <LiveHls
        cameraId={cameraId}
        cameraName={props.cameraName}
        className={props.className}
        onUnavailable={useStill}
        onStateChange={reportState}
        onVideoElement={props.onVideoElement}
      />
    );
  }
  return (
    <StillCamera
      cameraId={cameraId}
      cameraName={props.cameraName}
      className={props.className}
      onStateChange={reportState}
    />
  );
}

type FallbackProps = {
  cameraId: CameraKey | null;
  cameraName: string;
  className: string | undefined;
  onStateChange: ((state: LiveCameraState) => void) | undefined;
};

function LiveHls({
  cameraId,
  cameraName,
  className,
  onUnavailable,
  onStateChange,
  onVideoElement,
}: FallbackProps & {
  onUnavailable: () => void;
  onVideoElement: WhepVideoPlayerProps['onVideoElement'];
}): React.JSX.Element {
  const stream = useCameraStreamToken(cameraId);
  const video = React.useRef<HTMLVideoElement | null>(null);
  const bindVideo = React.useCallback(
    (element: HTMLVideoElement | null) => {
      video.current = element;
      onVideoElement?.(element);
    },
    [onVideoElement],
  );
  React.useEffect(() => {
    if (!cameraId || stream.isError) onUnavailable();
  }, [cameraId, stream.isError, onUnavailable]);
  React.useEffect(() => {
    let lastProgress = performance.now();
    let firstFrame = false;
    let frames = 0;
    let position = 0;
    return subscribeFrameHealth(() => {
      const now = performance.now();
      const element = video.current;
      const nextFrames = element ? readTotalVideoFrames(element) : 0;
      const nextPosition = element?.currentTime ?? 0;
      if ((nextFrames !== null && nextFrames > frames) || nextPosition > position) {
        firstFrame = true;
        lastProgress = now;
      }
      frames = nextFrames ?? frames;
      position = nextPosition;
      if (now - lastProgress > (firstFrame ? FRAME_STALL_AFTER_MS : 15_000)) onUnavailable();
    });
  }, [onUnavailable]);
  return (
    <HlsVideoPlayer
      streamUrl={stream.data?.url ?? null}
      cameraName={cameraName}
      {...(className ? { className } : {})}
      onUnavailable={onUnavailable}
      {...(onStateChange ? { onStateChange } : {})}
      onVideoElement={bindVideo}
    />
  );
}

function StillCamera({
  cameraId,
  cameraName,
  className,
  onStateChange,
}: FallbackProps): React.JSX.Element {
  const { t } = useTranslation('common');
  const { mutateAsync } = useCameraSnapshot();
  const [url, setUrl] = React.useState<string | null>(null);
  React.useEffect(() => {
    onStateChange?.('still');
  }, [onStateChange]);
  React.useEffect(() => {
    let cancelled = false;
    let revoke: (() => void) | undefined;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const refresh = async (): Promise<void> => {
      if (!cameraId) return;
      try {
        const snapshot = await mutateAsync({ cameraId });
        if (cancelled) {
          snapshot.revoke();
          return;
        }
        const previous = revoke;
        revoke = snapshot.revoke;
        setUrl(snapshot.objectUrl);
        previous?.();
      } catch {
        /* Keep the last successful still when the source is temporarily unavailable. */
      }
      if (!cancelled)
        timer = setTimeout(() => {
          void refresh();
        }, 5_000);
    };
    void refresh();
    return () => {
      cancelled = true;
      clearTimeout(timer);
      revoke?.();
    };
  }, [cameraId, mutateAsync]);
  return (
    <div className={`relative bg-black ${className ?? ''}`} data-testid="still-camera-player">
      {url ? <img src={url} alt={cameraName} className="h-full w-full object-contain" /> : null}
      <span
        className="absolute bottom-2 start-2 rounded bg-black/70 px-2 py-1 text-xs text-white"
        role="status"
      >
        {t('video.stillImage')}
      </span>
    </div>
  );
}
